import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LocalAudioTrack, Room, RoomEvent, Track } from 'livekit-client';
import {
  RECORDER_TIMESLICE_MS,
  ResumableRecordingUploader,
  captureConstraints,
  negotiateRecordingMimeType,
  recorderOptions,
  selectCaptureTier,
  stopMediaRecorderAndWait,
} from './continuousRecordingUpload';
import './LiveAiInterviewViewer.css';

/**
 * Candidate-facing owner of the permanent two-way interview browser lifecycle.
 *
 * Security-sensitive context is always reloaded by the backend from the
 * invitation capability. This component owns only consented media, LiveKit
 * transport, private recording upload, and sanitized candidate UI state.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || process.env.REACT_APP_BACKEND_URL || '';
const OPENING_ATTRIBUTE = 'jobseeqr.interview.opening_complete';
const COMPLETE_ATTRIBUTE = 'jobseeqr.interview.complete';
const STARTED_AT_ATTRIBUTE = 'jobseeqr.interview.started_at';
const AGENT_STATE_ATTRIBUTE = 'lk.agent.state';
const STARTUP_WARNING_MS = 20000;
const FINAL_UPLOAD_TIMEOUT_MS = 45000;

const INTERVIEW_COPY = {
  en: {
    unavailable: 'Interview unavailable', submitted: 'Your interview has been submitted',
    submittedBody: 'We are preparing the results. You can close this page now.',
    submittedClose: 'Close', closed: 'Interview complete',
    closedBody: 'Your interview has been saved. You can close this page now.',
    before: 'Before you begin', roleJoin: 'Up to', minutes: 'minutes', language: 'Interview language',
    english: 'English', arabic: 'Arabic — العربية', consent: 'I agree to take this AI interview. I understand that my camera, microphone, and spoken answers will be recorded and reviewed by the hiring team.',
    check: 'Test camera, microphone, and sound', start: 'Start interview', starting: 'Starting…',
    leaveBefore: 'Leave without starting', end: 'End interview', leave: 'Leave', finalizing: 'Saving your interview',
    agentSpeaking: 'AI interviewer is speaking', agentThinking: 'Please wait', yourTurn: 'Your turn', agentJoining: 'Getting the interviewer ready',
    openingDelayed: 'The interviewer is still joining. Please keep this page open; your interview has not started yet.'
  },
  ar: {
    unavailable: 'المقابلة غير متاحة', submitted: 'تم إرسال مقابلتك',
    submittedBody: 'نقوم بإعداد النتائج. يمكنك إغلاق هذه الصفحة الآن.',
    submittedClose: 'إغلاق', closed: 'اكتملت المقابلة',
    closedBody: 'تم حفظ مقابلتك. يمكنك إغلاق هذه الصفحة الآن.',
    before: 'قبل أن تبدأ', roleJoin: 'حتى', minutes: 'دقيقة', language: 'لغة المقابلة',
    english: 'English — الإنجليزية', arabic: 'العربية', consent: 'أوافق على إجراء هذه المقابلة بالذكاء الاصطناعي. أفهم أنه سيتم تسجيل الكاميرا والميكروفون وإجاباتي الصوتية ومراجعتها من فريق التوظيف.',
    check: 'فحص الكاميرا والميكروفون ومكبر الصوت', start: 'ابدأ المقابلة', starting: 'جارٍ البدء…',
    leaveBefore: 'المغادرة من دون بدء', end: 'إنهاء المقابلة', leave: 'مغادرة', finalizing: 'جارٍ حفظ المقابلة',
    agentSpeaking: 'المحاور يتحدث', agentThinking: 'يرجى الانتظار', yourTurn: 'دورك', agentJoining: 'جارٍ تجهيز المحاور',
    openingDelayed: 'لا يزال المحاور قيد الاتصال. يرجى إبقاء هذه الصفحة مفتوحة؛ لم تبدأ مقابلتك بعد.'
  }
};

/**
 * Call one JSON AI-interview API endpoint and normalize its error contract.
 * The helper never stores request bodies, tokens, or upload capabilities.
 * @param {string} path Backend-relative API path.
 * @param {RequestInit} options Fetch options.
 * @returns {Promise<object>} Parsed JSON response.
 */
async function request(path, options = {}) {
  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.detail || payload.error || 'The interview request failed.');
  return payload;
}

/** Format elapsed seconds as a stable `MM:SS` candidate timer. */
function formatTime(seconds) {
  const safe = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${String(Math.floor(safe / 60)).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`;
}

/**
 * Capture the settings the browser actually applied to the recorder stream.
 * Track settings take priority because devices may clamp requested values.
 */
function actualCaptureMetadata(stream, recorder, tier, selectedMimeType) {
  const settings = stream.getVideoTracks()[0]?.getSettings?.() || {};
  return {
    contentType: recorder.mimeType || selectedMimeType,
    codec: (recorder.mimeType || selectedMimeType).split('codecs=')[1] || '',
    width: Number(settings.width || tier.width),
    height: Number(settings.height || tier.height),
    frameRate: Number(settings.frameRate || tier.frameRate),
    videoBitsPerSecond: Number(recorder.videoBitsPerSecond || tier.videoBitsPerSecond),
    audioBitsPerSecond: Number(recorder.audioBitsPerSecond || tier.audioBitsPerSecond),
  };
}

/**
 * Create the LiveKit publication from the one consented microphone source.
 * Cloning is not a second capture; it allows opening-time publication muting
 * without muting MediaRecorder's copy.
 * @param {MediaStream} mediaStream Single consented camera/microphone stream.
 * @returns {LocalAudioTrack}
 */
export function createLiveKitAudioTrack(mediaStream, LocalAudioTrackImpl = LocalAudioTrack) {
  const sourceTrack = mediaStream.getAudioTracks()[0];
  if (!sourceTrack) throw new Error('The consented capture stream has no microphone track.');
  return new LocalAudioTrackImpl(sourceTrack.clone());
}

/** Build the only candidate-controlled session fields; language is server-validated. */
export function sessionStartPayload(invitationToken, language, startRequestId) {
  return { invitationToken, language, startRequestId };
}

/**
 * Report a delayed opening without converting transport latency into a valid
 * interview submission. The worker stays connected and the candidate's mic
 * stays muted until the real opening-complete handshake arrives.
 */
export function handleOpeningDelay(setWarning, _finishInterview, message = INTERVIEW_COPY.en.openingDelayed) {
  setWarning(message);
}

/** Attempt the only standards-compliant tab close and report browser refusal. */
export function requestCandidateWindowClose(targetWindow = window) {
  try {
    targetWindow.close();
    return Boolean(targetWindow.closed);
  } catch (_) {
    return false;
  }
}

/**
 * Attach the one consented stream to whichever preview element is currently
 * mounted. React replaces the preflight video with the live video, while a
 * MediaStream remains attached only to the original DOM element.
 */
export async function attachPreviewStream(videoElement, mediaStream) {
  if (!videoElement || !mediaStream) return false;
  if (videoElement.srcObject !== mediaStream) videoElement.srcObject = mediaStream;
  try {
    await videoElement.play?.();
  } catch (_) {
    // Muted inline playback normally succeeds; the stream remains attached if
    // this browser still requires a user gesture for play().
  }
  return true;
}

/** Read LiveKit's server-published agent state from an attribute event. */
export function agentStateFromAttributes(changed = {}, participant = null) {
  const value = changed[AGENT_STATE_ATTRIBUTE] || participant?.attributes?.[AGENT_STATE_ATTRIBUTE] || '';
  return ['initializing', 'idle', 'listening', 'thinking', 'speaking'].includes(value) ? value : '';
}

/**
 * Render and coordinate preflight, live conversation, recording, and finish.
 *
 * Phases are `loading -> preflight -> connecting -> live -> finalizing ->
 * submitted`. Error phases preserve explicit recovery paths. The subtle voice
 * cue mirrors LiveKit's server-published agent state; it never predicts turns.
 * @param {{invitationToken: string}} props Encrypted candidate capability.
 */
export default function LiveAiInterviewViewer({ invitationToken }) {
  useUiLanguage();
  const [phase, setPhase] = useState('loading');
  const [preflight, setPreflight] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [consented, setConsented] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [mediaReady, setMediaReady] = useState(false);
  const [starting, setStarting] = useState(false);
  const [agentState, setAgentState] = useState('initializing');
  const previewRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const roomRef = useRef(null);
  const localAudioRef = useRef(null);
  const recorderRef = useRef(null);
  const uploaderRef = useRef(null);
  const sessionRef = useRef(null);
  const recordingMetadataRef = useRef(null);
  const recordingStartedAtRef = useRef(0);
  const startedAtRef = useRef(0);
  const finishPromiseRef = useRef(null);
  const startupTimerRef = useRef(null);

  // Preflight returns disclosure/display data only. It neither consumes the
  // single interview attempt nor requests device permission.
  useEffect(() => {
    let active = true;
    request(`/ai-interviews/invitations/${encodeURIComponent(invitationToken)}/preflight`)
      .then((payload) => {
        if (!active) return;
        setPreflight(payload);
        setSelectedLanguage(payload.defaultLanguage === 'ar' ? 'ar' : 'en');
        setPhase('preflight');
      })
      .catch((requestError) => {
        if (!active) return;
        setError(requestError.message);
        setPhase('error');
      });
    return () => { active = false; };
  }, [invitationToken]);

  // The timer derives from the worker-published start timestamp instead of
  // treating browser connection state as authoritative.
  useEffect(() => {
    if (phase !== 'live' || !startedAtRef.current) return undefined;
    const update = () => setElapsed(Math.max(0, Math.floor((Date.now() - startedAtRef.current) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [phase]);

  /** Stop all local and remote media resources without submitting new state. */
  const releaseMedia = useCallback(() => {
    window.clearTimeout(startupTimerRef.current);
    roomRef.current?.disconnect?.();
    localAudioRef.current?.stop?.();
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    if (previewRef.current) previewRef.current.srcObject = null;
    roomRef.current = null;
    localAudioRef.current = null;
    streamRef.current = null;
  }, []);

  useEffect(() => () => releaseMedia(), [releaseMedia]);

  // Preflight and live phases render different video elements. Reattach the
  // existing stream after every relevant phase transition without requesting
  // camera or microphone permission a second time.
  useEffect(() => {
    if (!mediaReady || !streamRef.current) return;
    attachPreviewStream(previewRef.current, streamRef.current);
  }, [mediaReady, phase]);

  /**
   * Obtain the single consented stream and verify video/audio playback.
   * Failure occurs before reservation, so it consumes no interview attempt.
   */
  const testMedia = async () => {
    if (!consented) return;
    setError('');
    try {
      if (!streamRef.current) {
        const tier = selectCaptureTier();
        streamRef.current = await navigator.mediaDevices.getUserMedia(captureConstraints(tier));
      }
      if (previewRef.current) {
        previewRef.current.srcObject = streamRef.current;
        await previewRef.current.play();
      }
      const AudioContextImpl = window.AudioContext || window.webkitAudioContext;
      if (AudioContextImpl) {
        const context = new AudioContextImpl();
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        gain.gain.value = 0.035;
        oscillator.connect(gain).connect(context.destination);
        oscillator.start();
        oscillator.stop(context.currentTime + 0.12);
        window.setTimeout(() => context.close(), 300);
      }
      setMediaReady(true);
    } catch (mediaError) {
      setError(uiText('Please allow camera and microphone access. The interview has not started. Contact the hiring team if you need another interview format.'));
      setMediaReady(false);
    }
  };

  /**
   * Converge candidate, worker, timeout, and leave completion on one promise.
   * Recording finalization is attempted first, but its technical failure never
   * blocks transcript submission or evaluation.
   */
  const finishInterview = useCallback((reason = 'candidate_ended') => {
    if (finishPromiseRef.current) return finishPromiseRef.current;
    finishPromiseRef.current = (async () => {
      setPhase('finalizing');
      window.clearTimeout(startupTimerRef.current);
      const recorder = recorderRef.current;
      const uploader = uploaderRef.current;
      const session = sessionRef.current;
      let recordingFailure = null;
      try {
        if (recorder && recorder.state !== 'inactive') await stopMediaRecorderAndWait(recorder);
        if (uploader) {
          const uploadMetrics = await Promise.race([
            uploader.finalize(),
            new Promise((_, reject) => window.setTimeout(() => reject(new Error('The final recording bytes are still uploading. Keep this page open and retry.')), FINAL_UPLOAD_TIMEOUT_MS)),
          ]);
          const metadata = recordingMetadataRef.current;
          await request(`/ai-interviews/sessions/${encodeURIComponent(session.sessionId)}/recording-finalize`, {
            method: 'POST',
            body: JSON.stringify({
              invitationToken,
              ...metadata,
              bytes: uploadMetrics.bytesProduced,
              durationSeconds: Math.max(0, Math.round((Date.now() - recordingStartedAtRef.current) / 1000)),
            }),
          });
        }
      } catch (recordingError) {
        recordingFailure = recordingError;
      }
      try {
        await request(`/ai-interviews/sessions/${encodeURIComponent(session.sessionId)}/end`, {
          method: 'POST',
          body: JSON.stringify({ invitationToken, reason: recordingFailure ? `${reason}:recording_technical_failure` : reason }),
        });
      } catch (finishError) {
        setError(finishError.message || 'We could not save the interview. Please try again.');
        setPhase('finalize-error');
        finishPromiseRef.current = null;
        throw finishError;
      }
      releaseMedia();
      if (recordingFailure) {
        setError('Your answers were submitted and the results are being prepared. The recording still needs attention. Please retry uploading the recording.');
        setPhase('finalize-error');
        finishPromiseRef.current = null;
        return;
      }
      setPhase('submitted');
    })();
    return finishPromiseRef.current;
  }, [invitationToken, releaseMedia]);

  /**
   * Join the exact LiveKit room and enforce opening-before-unmute.
   * MediaRecorder starts after publication is established; the microphone stays
   * muted until remote audio and the worker opening attribute have both arrived.
   */
  const connectRoom = async (session, mediaStream, mediaRecorder) => {
    const room = new Room({ adaptiveStream: true, disconnectOnPageLeave: false });
    roomRef.current = room;
    let openingComplete = false;
    let remoteAudioAttached = false;
    /** Unmute only after both independent opening playout conditions hold. */
    const maybeReady = async () => {
      if (!openingComplete || !remoteAudioAttached) return;
      window.clearTimeout(startupTimerRef.current);
      setWarning('');
      await localAudioRef.current?.unmute?.();
      setPhase('live');
    };
    room.on(RoomEvent.TrackSubscribed, async (track, _publication, participant) => {
      if (track.kind !== Track.Kind.Audio) return;
      const publishedState = agentStateFromAttributes({}, participant);
      if (publishedState) setAgentState(publishedState);
      track.attach(audioRef.current);
      await room.startAudio();
      await audioRef.current?.play?.();
      remoteAudioAttached = true;
      await maybeReady();
    });
    room.on(RoomEvent.ParticipantAttributesChanged, async (changed, participant) => {
      const publishedState = agentStateFromAttributes(changed, participant);
      if (publishedState) setAgentState(publishedState);
      if (changed[COMPLETE_ATTRIBUTE] === 'true') {
        finishInterview('completed_by_interviewer').catch(() => {});
        return;
      }
      if (changed[STARTED_AT_ATTRIBUTE]) startedAtRef.current = Number(changed[STARTED_AT_ATTRIBUTE]) || Date.now();
      if (changed[OPENING_ATTRIBUTE] === 'true') {
        openingComplete = true;
        if (!publishedState) setAgentState('listening');
        if (!startedAtRef.current) startedAtRef.current = Date.now();
        await maybeReady();
      }
    });
    room.on(RoomEvent.Disconnected, () => {
      if (!finishPromiseRef.current && phase === 'live') {
        setWarning('The sound connection was interrupted. We are reconnecting. Your recording is still saved.');
      }
    });

    // Clone the single consented microphone track for LiveKit. Muting the
    // publication during the opening must not silence MediaRecorder's copy.
    const localAudio = createLiveKitAudioTrack(mediaStream);
    localAudioRef.current = localAudio;
    await localAudio.mute();
    await room.connect(session.livekitUrl, session.participantToken, { autoSubscribe: true });
    room.remoteParticipants?.forEach?.((participant) => {
      const publishedState = agentStateFromAttributes({}, participant);
      if (publishedState) setAgentState(publishedState);
    });
    await room.localParticipant.publishTrack(localAudio, { source: Track.Source.Microphone });
    mediaRecorder.start(RECORDER_TIMESLICE_MS);
    recordingStartedAtRef.current = Date.now();
    startupTimerRef.current = window.setTimeout(() => {
      handleOpeningDelay(setWarning, finishInterview, copy.openingDelayed);
    }, STARTUP_WARNING_MS);
  };

  /**
   * Reserve the session, initialize private upload, then join LiveKit.
   * Recorder blobs pass directly to the ordered uploader; this component never
   * accumulates a full-interview chunk array.
   */
  const startInterview = async () => {
    if (!mediaReady || !streamRef.current || starting) return;
    setStarting(true);
    setError('');
    setPhase('connecting');
    try {
      const session = await request('/ai-interviews/sessions', {
        method: 'POST',
        body: JSON.stringify(sessionStartPayload(
          invitationToken,
          selectedLanguage,
          crypto.randomUUID?.() || `${Date.now()}`
        )),
      });
      sessionRef.current = session;
      const tier = selectCaptureTier();
      const selectedMimeType = negotiateRecordingMimeType();
      const mediaRecorder = new MediaRecorder(streamRef.current, recorderOptions(selectedMimeType, tier));
      recorderRef.current = mediaRecorder;
      const metadata = actualCaptureMetadata(streamRef.current, mediaRecorder, tier, selectedMimeType);
      recordingMetadataRef.current = metadata;
      const upload = await request(`/ai-interviews/sessions/${encodeURIComponent(session.sessionId)}/recording-upload`, {
        method: 'POST',
        body: JSON.stringify({ invitationToken, ...metadata }),
      });
      const uploader = new ResumableRecordingUploader({
        uploadUrl: upload.uploadUrl,
        contentType: upload.contentType,
        sessionId: session.sessionId,
        onMetrics: (metrics) => {
          if (metrics.pendingBytes > 24 * 1024 * 1024 || metrics.oldestPendingAge > 15000) {
            setWarning('Your video is uploading slowly. You can continue the interview while we keep saving it.');
          } else if (metrics.pendingBytes < 8 * 1024 * 1024) {
            setWarning('');
          }
        },
      });
      uploaderRef.current = uploader;
      mediaRecorder.addEventListener('dataavailable', (event) => {
        if (event.data?.size) uploader.enqueue(event.data).catch((uploadError) => setWarning(uploadError.message));
      });
      mediaRecorder.addEventListener('error', () => setWarning('There is a problem with the recording. This will not lower your interview result.'));
      await connectRoom(session, streamRef.current, mediaRecorder);
    } catch (startError) {
      setError(startError.message || 'The interview could not start.');
      setPhase('preflight');
      const session = sessionRef.current;
      if (session?.sessionId) {
        request(`/ai-interviews/sessions/${encodeURIComponent(session.sessionId)}/recording-upload`, {
          method: 'DELETE', body: JSON.stringify({ invitationToken }),
        }).catch(() => {});
      }
    } finally {
      setStarting(false);
    }
  };

  /** Finalize active work, then ask the browser to close without redirecting. */
  const leave = () => {
    const closeOrShowSafeState = () => {
      if (!requestCandidateWindowClose(window)) setPhase('closed');
    };
    if (phase === 'live' || phase === 'connecting') {
      finishInterview('candidate_left').then(closeOrShowSafeState).catch(() => {});
      return;
    }
    releaseMedia();
    closeOrShowSafeState();
  };

  const copy = INTERVIEW_COPY[selectedLanguage] || INTERVIEW_COPY.en;
  const pageDirection = selectedLanguage === 'ar' ? 'rtl' : 'ltr';
  const disclosures = preflight?.disclosuresByLanguage?.[selectedLanguage] || preflight?.disclosures || {};
  const cueState = phase === 'connecting' || agentState === 'initializing' ? 'initializing' : agentState === 'speaking' ? 'speaking' : agentState === 'thinking' ? 'thinking' : 'listening';
  const cueLabel = cueState === 'speaking' ? copy.agentSpeaking : cueState === 'thinking' ? copy.agentThinking : cueState === 'listening' ? copy.yourTurn : copy.agentJoining;

  if (phase === 'loading') return <main className="lai-shell"><div className="lai-loader" aria-label={uiText("Loading interview")} /></main>;
  if (phase === 'error') return <main className="lai-shell" dir={pageDirection}><section className="lai-card lai-error"><h1>{copy.unavailable}</h1><p>{uiText(error, { lng: selectedLanguage })}</p></section></main>;

  if (phase === 'submitted') {
    return (
      <main className="lai-shell" dir={pageDirection}>
        <section className="lai-final" aria-live="polite">
          <div className="lai-loader" />
          <h1>{copy.submitted}</h1>
          <p>{copy.submittedBody}</p>
          <button type="button" className="lai-secondary" onClick={leave}>{copy.submittedClose}</button>
        </section>
      </main>
    );
  }

  if (phase === 'closed') {
    return (
      <main className="lai-shell" dir={pageDirection}>
        <section className="lai-final lai-final--closed" aria-live="polite">
          <i className="fa-solid fa-circle-check" aria-hidden="true" />
          <h1>{copy.closed}</h1>
          <p>{copy.closedBody}</p>
        </section>
      </main>
    );
  }

  if (['live', 'connecting', 'finalizing', 'finalize-error'].includes(phase)) {
    return (
      <main className="lai-shell lai-shell--live" dir={pageDirection}>
        <audio ref={audioRef} autoPlay playsInline />
        <section className="lai-live-card">
          <div className="lai-preview-wrap">
            <video ref={previewRef} muted autoPlay playsInline />
            <span className="lai-timer">{formatTime(elapsed)}</span>
            {!['finalizing', 'finalize-error'].includes(phase) && <div className={`lai-agent-cue is-${cueState}`} role="status" aria-live="polite">
              <span className="lai-agent-orb" aria-hidden="true">
                {cueState === 'speaking' && <span className="lai-wave-bars"><i /><i /><i /><i /></span>}
                {cueState === 'thinking' && <span className="lai-thinking-dots"><i /><i /><i /></span>}
                {cueState === 'listening' && <i className="fas fa-microphone" />}
                {cueState === 'initializing' && <i className="fas fa-robot" />}
              </span>
              <span>{cueLabel}</span>
            </div>}
          </div>
          {warning && <p className="lai-warning" role="status">{warning}</p>}
          {error && <p className="lai-error-copy" role="alert">{uiText(error, { lng: selectedLanguage })}</p>}
          {phase === 'finalizing' && <div className="lai-loader" aria-label={copy.finalizing} />}
          <div className="lai-controls">
            <button type="button" className="lai-danger" onClick={() => finishInterview('candidate_ended').catch(() => {})} disabled={phase === 'finalizing'}>{copy.end}</button>
            <button type="button" className="lai-secondary" onClick={leave} disabled={phase === 'finalizing'}>{copy.leave}</button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="lai-shell" dir={pageDirection}>
      <section className="lai-card">
        <div className="lai-intro-head">
          <span className="lai-intro-icon" data-testid="ai-interviewer-icon" aria-hidden="true"><i className="fas fa-robot" /></span>
          <div>
            <p className="lai-eyebrow">{preflight?.companyName}</p>
            <h1>{preflight?.assessmentTitle}</h1>
            <p className="lai-role">{preflight?.jobTitle} · {copy.roleJoin} {preflight?.durationMinutes} {copy.minutes}</p>
          </div>
        </div>
        {(preflight?.allowedLanguages || []).length > 1 && <div className="lai-language-picker">
          <span>{copy.language}</span>
          <div>
            <button type="button" className={selectedLanguage === 'en' ? 'is-active' : ''} onClick={() => setSelectedLanguage('en')}>{copy.english}</button>
            <button type="button" className={selectedLanguage === 'ar' ? 'is-active' : ''} onClick={() => setSelectedLanguage('ar')}>{copy.arabic}</button>
          </div>
        </div>}
        <div className="lai-disclosures">
          <h2>{copy.before}</h2>
          {Object.values(disclosures).map((text) => <p key={text}>{text}</p>)}
        </div>
        <label className="lai-consent">
          <input type="checkbox" checked={consented} onChange={(event) => setConsented(event.target.checked)} />
          <span>{copy.consent}</span>
        </label>
        <div className="lai-preflight-preview"><video ref={previewRef} muted playsInline /></div>
        {error && <p className="lai-error-copy" role="alert">{uiText(error, { lng: selectedLanguage })}</p>}
        <div className="lai-actions">
          <button type="button" className="lai-secondary" disabled={!consented} onClick={testMedia}>{copy.check}</button>
          <button type="button" className="lai-primary" disabled={!consented || !mediaReady || starting} onClick={startInterview}>{starting ? copy.starting : copy.start}</button>
          <button type="button" className="lai-link" onClick={leave}>{copy.leaveBefore}</button>
        </div>
      </section>
    </main>
  );
}
